import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { DocumentTemplate } from "@visitproof/types";
import { DocumentTemplateService } from "../services/DocumentTemplateService";
import { useAccount } from "./AccountContext";

interface DocumentTemplateContextProps {
  documentTemplateList: DocumentTemplate[];
  selectedDocumentTemplate: DocumentTemplate | null;
  setSelectedDocumentTemplate: (documentTemplateId: string) => void;
  reloadDocumentTemplateList: () => void;
}

const DocumentTemplateContext = createContext<
  DocumentTemplateContextProps | undefined
>(undefined);

export const useDocumentTemplate = (): DocumentTemplateContextProps => {
  const context = useContext(DocumentTemplateContext);
  if (!context) {
    throw new Error(
      "useDocumentTemplate must be used within a DocumentTemplateProvider",
    );
  }
  return context;
};

export const DocumentTemplateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { account } = useAccount();
  const [documentTemplateList, setDocumentTemplateList] = useState<
    DocumentTemplate[]
  >([]);
  const [selectedDocumentTemplate, setSelectedDocumentTemplate] =
    useState<DocumentTemplate | null>(null);

  const reloadDocumentTemplateList = useCallback(async () => {
    if (account?.id) {
      const documentTemplateList =
        await DocumentTemplateService.getAllDocumentTemplates(account.id);
      setDocumentTemplateList(documentTemplateList);
    }
  }, [account?.id]);

  useEffect(() => {
    reloadDocumentTemplateList();
  }, [reloadDocumentTemplateList]);

  const setSelectedDocumentTemplateById = async (
    documentTemplateId: string,
  ) => {
    const documentTemplateData =
      await DocumentTemplateService.getDocumentTemplate(documentTemplateId);
    setSelectedDocumentTemplate(documentTemplateData);
  };

  return (
    <DocumentTemplateContext.Provider
      value={{
        documentTemplateList,
        selectedDocumentTemplate,
        setSelectedDocumentTemplate: setSelectedDocumentTemplateById,
        reloadDocumentTemplateList,
      }}
    >
      {children}
    </DocumentTemplateContext.Provider>
  );
};
